import { ApolloClient, ApolloLink, HttpLink, Operation, split, fromPromise } from '@apollo/client';
import { InMemoryCache, NormalizedCacheObject } from '@apollo/client/cache';
import { getMainDefinition, relayStylePagination } from '@apollo/client/utilities';
import { BatchHttpLink } from '@apollo/client/link/batch-http';
import { onError } from '@apollo/client/link/error';
import { setContext } from '@apollo/client/link/context';
import rollbar from 'assets/util/rollbar';

let rootPath;
const ENV = process.env.NODE_ENV;

if (ENV === 'development') {
  rootPath = 'http://localhost:3000';
} else if (ENV === 'staging') {
  rootPath = 'https://staging.sumzero.com';
} else if (ENV === 'circle' || ENV === 'test') {
  rootPath = 'http://' + window.location.hostname + ':' + window.location.port;
} else {
  rootPath = window.location.origin;
}

let csrfToken = '';
const authLink = setContext((_, { headers }) => {
  if (!csrfToken) {
    const metaTag = document.querySelector('meta[name="csrf-token"]');
    csrfToken = metaTag?.getAttribute('content') ?? '';
  }

  return {
    headers: {
      ...headers,
      'X-CSRF-TOKEN': csrfToken
    }
  };
});

let refreshPromise: Promise<void> | null = null;
const refreshCSRFToken = (): Promise<void> => {
  if (refreshPromise) return refreshPromise;

  refreshPromise = fetch(`${rootPath}/csrf_token`, { credentials: 'same-origin' })
    .then(res => {
      if (!res.ok) throw new Error('CSRF refresh failed');
      return res.json();
    })
    .then(data => {
      csrfToken = data.token;
      return;
    })
    .finally(() => {
      refreshPromise = null;
    });

  return refreshPromise;
};

const mutedErrors = [
  'Error: Response not successful: Received status code 401',
  'TypeError: cancelled',
  'TypeError: Could not connect to the server.',
  'TypeError: Failed to fetch',
  'TypeError: Load failed',
  'TypeError: Network request failed',
  'TypeError: NetworkError when attempting to fetch resource.',
  'TypeError: The network connection was lost.',
  'TypeError: Type error',
  'Uncaught TypeError: Illegal invocation',
  'Unexpected token N in JSON at position 0',
  'ServerError: Response not successful: Received status code 422',
  'ServerError: Response not successful: Received status code 503'
];

const mutedGraphQLErrors = [
  'was provided invalid value',
  'User with email already exists in database'
];

const shouldIgnore = (message: string): boolean =>
  mutedGraphQLErrors.find(err => message.includes(err)) ? true : false;

const errorHandler = onError(({ graphQLErrors, networkError, operation, forward }) => {
  if (ENV === 'circle') return;

  if (networkError) {
    const statusCode = 'statusCode' in networkError ? networkError.statusCode : null;

    if (statusCode === 422) {
      return fromPromise(refreshCSRFToken()).flatMap(() => forward(operation));
    }

    // eslint-disable-next-line no-console
    console.log(`[Network error]: ${networkError}, OperationName: ${operation.operationName}`);
    // Mutes failed to fetch errors
    if (mutedErrors.indexOf(String(networkError)) === -1) {
      rollbar.error(
        `[Apollo GraphQL PUBLIC network error] ${networkError}, OperationName: ${operation.operationName}`,
        { error: networkError, operation: operation }
      );
    }
  }

  if (graphQLErrors) {
    graphQLErrors.forEach(({ message, locations, path }) =>
      // eslint-disable-next-line no-console
      console.log(
        `[GraphQL error]: Message: ${message}, Location: ${locations}, Path: ${path}, OperationName: ${operation.operationName}`
      )
    );

    graphQLErrors
      .filter(({ message }) => !shouldIgnore(message))
      .forEach(error =>
        rollbar.error(
          `[Apollo GraphQL PUBLIC error] Message: ${error.message}, OperationName: ${operation.operationName}`,
          { error, operation }
        )
      );
  }

  return;
});

const batchHttpLink = new BatchHttpLink({
  uri: `${rootPath}/public_queries`,
  credentials: 'same-origin'
});

const httpLink = new HttpLink({
  uri: `${rootPath}/public_queries`,
  credentials: 'same-origin'
});

const isIdeaPositionsPricingOperation = ({ query }: Operation): boolean => {
  const definition = getMainDefinition(query);
  return (
    definition.kind === 'OperationDefinition' && definition.name?.value === 'IdeaPositionsPricing'
  );
};

const generatePublicClient = (): ApolloClient<NormalizedCacheObject> => {
  return new ApolloClient({
    devtools: {
      enabled: ENV === 'development'
    },
    link: ApolloLink.from([
      errorHandler,
      authLink,
      split(isIdeaPositionsPricingOperation, httpLink, batchHttpLink)
    ]),
    cache: new InMemoryCache({
      possibleTypes: {
        FeedItem: ['Idea', 'IdeaUpdate']
      },
      typePolicies: {
        Query: {
          fields: {
            feedItems: relayStylePagination(),
            lettersForFund: relayStylePagination(['fundName']),
            mediaIndexList: relayStylePagination(['indexType', 'type', 'searchParams']),
            currentRanksForCategory: relayStylePagination(['category'])
          }
        }
      }
    })
  });
};

export default generatePublicClient;
